import React, { useState, useEffect, useRef } from 'react'
import { debounce } from '@helpers/global'
import { ProductSimple, ProductVariantSimple } from '@interfaces/product'
import Image from '@components/atoms/Image'
import Icon from '@components/atoms/Icon'
import { handleAddToCart } from '@helpers/global'
import { trackAddToCart } from '@helpers/trackCartActions'
import Modal from './Modal'

interface ProductProps {
  product: ProductSimple
  selectedVariant?: ProductVariantSimple
  className?: string
  selectedFilters?: { [key: string]: string[] }
  enableQuickAdd?: boolean
}

const AddToCartButton: React.FC<{
  currentVariant: ProductVariantSimple
  currentProduct: ProductSimple
  onClick: () => void
  handleNotifyClick: () => void
}> = ({ currentVariant, currentProduct, onClick, handleNotifyClick }) => {
  const { available } = currentVariant
  const comingSoonProduct = currentVariant.metafields.badges.find((badge) => badge.name.includes('Coming'))
  const BISProduct = {
    id: currentProduct.id,
    handle: currentProduct.handle,
    available: available,
    tags: currentProduct.tags,
    variants: currentProduct.variants.map((variant) => ({
      id: variant.id,
      available: variant.available,
      title: variant.title,
      inventory_quantity: variant.inventory_quantity,
      inventory_management: variant.inventory_management,
    })),
  }

  const button = (
    <button
      type="button"
      className={`${
        comingSoonProduct ? 'button--primary body-xxs px-2 text-white': available ? 'bg-green-1 shadow-inner-green w-8' : 'bg-disabled shadow-inner-grey pointer-events-none text-black body-xxs w-8'
      } flex items-center justify-center rounded-full h-8`}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        if(comingSoonProduct) {
          handleNotifyClick()
        } else if (available && !comingSoonProduct) {
          onClick()
        }

      }}
      data-product-data={`${JSON.stringify(BISProduct)}`}
      data-variant-id={currentVariant.id}
      aria-label="Add To Cart"
    >
      {comingSoonProduct ? (
        window.DEPT.Strings.product.notifyMe
      ) : (
        <Icon name="cart" className="text-black pointer-events-none" />
      )}
    </button>
  )

  return available ? (
    button
  ) : (
    <a
      href="#"
      className="BIS_trigger flex"
      data-product-data={`${JSON.stringify(BISProduct)}`}
      data-variant-id={currentVariant.id}
      aria-label="Notify Me When Available"
    >
      {button}
    </a>
  )
}

const ProductCard: React.FC<ProductProps> = ({ product, selectedVariant, selectedFilters = {}, className = '' , enableQuickAdd}) => {
  const { title, handle, variants, placeholder_svg } = product;
  const userSelectedVariant = useRef(false);

  const [currentVariant, setCurrentVariant] = useState<ProductVariantSimple>(selectedVariant || variants?.[0]);
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const [thumbnailActive, setThumbnailActive] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const sizeSelectorRef = useRef<HTMLDivElement | null>(null);
  //Coming Soon Modal
  const [isModalOpen, setIsModalOpen] = useState(false)
  const openModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)
  const handleNotifyClick = () => openModal()

  // Utility function to check if a variant matches the selected filters
  const isVariantMatchingFilters = (variant: ProductVariantSimple, filters: { [key: string]: string[] }) => {
    return Object.entries(filters).every(([paramName, values]) => {
      //Convert price from money to a number
      const parsedPrice = typeof variant.price === 'string'
        ? parseFloat(variant.price.replace(/[^0-9.]/g, ''))
        : variant.price
      switch (paramName) {
        case 'filter.v.m.filters.lens_tech':
          return variant.metafields?.lensTech?.some((item) => values.includes(item.name)) || false
        case 'filter.v.m.filters.frame_color':
          return variant.metafields?.frameColor?.some((item) => values.includes(item.name)) || false
        case 'filter.v.m.filters.lens_color':
          return variant.metafields?.lensColor?.some((item) => values.includes(item.name)) || false
        case 'filter.v.m.filters.light_transmission':
          return variant.metafields?.lightTransmission?.some((item) => values.includes(item.name)) || false
        case 'filter.v.m.filters.sports':
          return variant.metafields?.sports?.some((item) => values.includes(item.name)) || false
        case 'filter.v.m.filters.on_sale':
          return variant.salePrice || false
        case 'filter.v.price':
          return values.some(value => {
            const [minPrice, maxPrice] = value.split('-');
            const min = parseFloat(minPrice);
            const max = parseFloat(maxPrice);
            return parsedPrice >= min && parsedPrice <= max;
          })
        default:
          // Default check for variant options
          return variant.options.some((option) => values.includes(option))
      }
    })
  }

  // Update current variant when selectedFilters change
  useEffect(() => {
    if (userSelectedVariant.current) {
      userSelectedVariant.current = false;
      return;
    }

    const findAvailableVariant = () => {
      const pathname = window.location.pathname.toLowerCase();
      // This function is used to display the product variant that matches the current collection, usually via the URL pathname, with some exceptions. If the URL of the PLP matches a string in the collectionKeywords array, we need to look them up differently. This can usually be accomplished either by matching hardcoded variant names (see 'safety' collection logic), or by matching product badges (see 'sale' collection logic). Falls back to first in stock variant.
      const collectionKeywords = ['polarized', 'photochromic', 'safety', 'new', 'sale', 'best', 'coming', 'black'];

      const keywordPath = collectionKeywords.find(keyword => pathname.includes(keyword));
      const availableVariant = variants.find((variant) => variant.available)
      if(keywordPath){
        let matchingPlpVariant ;
        if(keywordPath === 'safety'){
          matchingPlpVariant = variants.find((variant) => variant.options.find(vari => vari.includes('Z87+') || vari.includes('BALL-')));
          return matchingPlpVariant || availableVariant || variants?.[0];
        }
        else if(keywordPath === 'new' || keywordPath === 'coming'){
          matchingPlpVariant = variants.find((variant) =>
            variant.metafields.badges.find(badge => badge.name.toLowerCase().includes(keywordPath)));
          return matchingPlpVariant || availableVariant || variants?.[0];
        }
        else if(keywordPath === 'sale'){
          matchingPlpVariant = variants.find((variant) =>
            variant.salePrice != '');
          return matchingPlpVariant || availableVariant || variants?.[0];
        }
        else if(keywordPath === 'best'){
          const bestVariantTag = product.tags?.find((tag) => tag.includes('best_'));
            if (bestVariantTag) {
              matchingPlpVariant = variants.find(variant => variant.options.find(option => option.includes(bestVariantTag.slice(5))));
            }
          return matchingPlpVariant || availableVariant || variants?.[0];
        }
        else if(keywordPath === 'black'){
          matchingPlpVariant = variants.find((variant) => variant.options.find(vari => vari.includes('Smoke') || vari.includes('Smoke')));
          return matchingPlpVariant || availableVariant || variants?.[0];
        }
        else {
          matchingPlpVariant = variants.find((variant) =>variant.options.find(title => title.toLowerCase().includes(keywordPath)));
          return matchingPlpVariant || availableVariant || variants?.[0];
        }
      } else {
        return availableVariant || variants?.[0]
      }
    }

    if (selectedFilters && Object.keys(selectedFilters).length > 0) {
      const matchingVariant = variants.find((variant) => isVariantMatchingFilters(variant, selectedFilters))
      setCurrentVariant((matchingVariant || findAvailableVariant() as ProductVariantSimple))
    } else {
      setCurrentVariant((findAvailableVariant() as ProductVariantSimple))
    }
  }, [selectedFilters, variants])

  useEffect(() => {
    setAnimationClass('fade-in')
  }, [])

  const handleThumbnailClick = (variant: ProductVariantSimple) => {
    userSelectedVariant.current = true;
    setCurrentVariant(variant)
  }

  const handleAddToCartClick = () => {
    const hasSizeOption = product.optionsWithValues?.some((option) => option.name.toLowerCase() === 'size')
    if (hasSizeOption) {
      setShowSizeSelector(true)
    } else {
      handleAddToCart(
        currentVariant.id,
        1,
        currentVariant.metafields?.relatedVariants
      );
      trackAddToCart(product, currentVariant)
    }
  }

  const handleSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSize = event.target.value
    const matchingVariant = variants.find((variant) =>
      variant.options.every((option, index) => {
        if (product.optionsWithValues?.[index]?.name.toLowerCase() === 'size') {
          return option === selectedSize
        }
        return option === currentVariant.options[index]
      }),
    )
    if (matchingVariant) {
      setCurrentVariant(matchingVariant)
    }
  }

  const renderThumbnails = () => {
    if (!variants || !product.optionsWithValues) return null

    // Find the "Lens Type" option
    const lensTypeOption = product.optionsWithValues.find((option) => option.name === 'Lens Type')

    // If no "Lens Type" option exists, return null
    if (!lensTypeOption) return null

    // Use nullish coalescing to handle undefined `values`
    const lensTypeValues = lensTypeOption.values ?? lensTypeOption.value

    // If no valid `lensTypeValues`, return null
    if (!lensTypeValues || lensTypeValues.length === 0) return null

    // Deduplicate variants based on "Lens Type"
    const uniqueLensTypeVariants: ProductVariantSimple[] = []
    const seenLensTypes = new Set<string>()

    for (const variant of variants) {
      const lensTypeValue = variant.options.find((option) => lensTypeValues.includes(option))
      if (lensTypeValue && !seenLensTypes.has(lensTypeValue)) {
        seenLensTypes.add(lensTypeValue)
        uniqueLensTypeVariants.push(variant)
      }
    }

    // Only show thumbnails if there are at least two unique variants
    if (uniqueLensTypeVariants.length < 2) return null

    const maxThumbnailsDesktop = 5
    const maxThumbnailsMobile = 4
    const isDesktop = window.innerWidth >= 768
    const maxThumbnails = isDesktop ? maxThumbnailsDesktop : maxThumbnailsMobile

    const thumbnails = uniqueLensTypeVariants.slice(0, maxThumbnails)
    const remainingVariants = uniqueLensTypeVariants.length - maxThumbnails

    // Get the current lens type of the selected variant
    const currentLensType = currentVariant.options.find((option) => lensTypeValues.includes(option))

    return (
      <div className="flex items-center justify-center gap-2.5">
        {thumbnails.map((variant) => {
          // Check if this variant's lens type matches the current lens type
          const variantLensType = variant.options.find((option) => lensTypeValues.includes(option))
          const isActive = variantLensType === currentLensType

          return (
            <button
              key={variant.id}
              type="button"
              className={`bg-white rounded-sm shadow-thumbnail border ${
                isActive ? 'border-blue-1' : 'border-transparent'
              }`}
              onClick={() => {
                handleThumbnailClick(variant)
                setThumbnailActive(true)
              }}
              onFocus={() => setThumbnailActive(true)}>
              <Image
                alt={variant.image?.alt || ''}
                src={variant.image?.src || ''}
                srcWidths={{ sm: 50 }}
                height={50}
                width={50}
                classes="w-full h-auto cursor-pointer rounded-sm aspect-square object-contain max-w-9"
                blendMode="multiply"
              />
            </button>
          )
        })}
        {remainingVariants > 0 && (
          <a href={`/products/${handle}`} className="cursor-pointer w-9 h-9 flex items-center justify-center">
            <p className="text-black body-xs">+{remainingVariants}</p>
          </a>
        )}
      </div>
    )
  }

  const renderBadges = () => {
    if (!currentVariant.metafields?.badges.length) return null

    return (
      <div className="absolute z-1 left-0 top-0 flex items-center flex-wrap gap-1">
        {currentVariant.metafields.badges.map((badge, index) => (
          <div
            key={index}
            className="rounded-full px-3 py-px body-xxs h-4.5 flex items-center"
            style={{
              color: badge.copyColor || '#000000',
              background: badge.gradient || badge.bgColor,
            }}
          >
            {badge.image ? <Image src={badge.image} alt={badge.name} classes="h-7"/> : badge.name}
          </div>
        ))}
      </div>
    )
  }

  //MIK 9/2/2025 - COMMENTING OUT TO HIDE VARIANT TITLE ON PRODUCT CARDS - KEEPING IN CASE WE WANT IT BACK
  // const renderFilteredOptions = () => {
  //   if (!product.optionsWithValues?.length || !currentVariant) return null
  //
  //   const filteredOptions = product.optionsWithValues
  //     .filter((option) => option.name.toLowerCase() !== 'size') // Exclude "Size" options
  //     .map((option) => {
  //       const currentValue = currentVariant.options.find(
  //         (opt) => option.values?.includes(opt) && opt.toLowerCase() !== 'default title', // Exclude "Default Title" options
  //       )
  //       if (!currentValue) return null // Skip if no matching value in the current variant
  //       return (
  //         <a href={currentVariant.url} key={`${option.name}-${currentValue}`}>
  //         <span key={`${option.name}-${currentValue}`} className="body-xxs font-light mt-3" id={`${metafields?.mainCategory}`}>
  //           {currentValue}
  //         </span>
  //       </a>
  //       )
  //     })
  //     .filter(Boolean) // Remove nulls
  //
  //   if (!filteredOptions.length) return null
  //
  //   return <div className="flex items-center gap-2 flex-wrap justify-center">{filteredOptions}</div>
  // }

  const renderSizeSelector = () => {
    const sizeOption = product.optionsWithValues?.find((option) => option.name.toLowerCase() === 'size')
    if (!sizeOption || !sizeOption.values?.length || !showSizeSelector) return null

    return (
      <div
        className="absolute z-2 bottom-12 left-0 right-0 px-2 transition-transform transform fade-in"
        ref={sizeSelectorRef}
      >
        <div className="bg-black bg-opacity-80 rounded-lg p-2">
          <p className="body-xs text-grey-5 mb-1">{window.DEPT.Strings.productCard.selectSize}</p>
          <select
            value={currentVariant.options.find((option) => sizeOption.values?.includes(option)) || ''}
            onChange={handleSizeChange}
            className="h-8 body-xs text-black bg-grey-5 p-1 pl-3 w-full border border-grey-4 rounded-full shadow-search mb-8 focus:outline-none focus:ring-0"
          >
            {sizeOption.values.map((size) => {
              const isAvailable = variants.some((variant) => variant.options.includes(size) && variant.available)

              return (
                <option key={size} value={size}>
                  {size} {!isAvailable && window.DEPT.Strings.productCard.soldOut}
                </option>
              )
            })}
          </select>
          <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:justify-between">
            <a
              className="body-xs text-grey-5 underline"
              href={`${currentVariant.url}#size-guide-pdp`}
              aria-label={window.DEPT.Strings.productCard.sizeGuide}
            >
              {window.DEPT.Strings.productCard.sizeGuide}
            </a>
            <button
              type="button"
              className={`body-xs flex items-center justify-center rounded-full border md:body-sm ${
                currentVariant.available
                  ? 'cursor-pointer border-grey-5 bg-transparent text-grey-5 hover:bg-grey-5 hover:text-black'
                  : 'cursor-not-allowed border-disabled bg-disabled text-black'
              } py-2.5 px-6`}
              disabled={!currentVariant.available}
              onClick={() => {
                handleAddToCart(
                  currentVariant.id,
                  1,
                  currentVariant.metafields?.relatedVariants
                );
                trackAddToCart(product, currentVariant);
                setTimeout(() => setShowSizeSelector(false), 300);
              }}
            >
              {window.DEPT.Strings.productCard.addToCart}
            </button>
          </div>
        </div>
      </div>
    )
  }

  useEffect(() => {
    const debouncedClose = debounce(() => {
      if (!currentVariant.available) {
        // Find the first available variant
        const firstAvailableVariant = variants.find((variant) => variant.available);

        // Update the current variant to the first available one
        if (firstAvailableVariant) {
          setCurrentVariant(firstAvailableVariant)
        }
      }

      // Close the size selector
      setShowSizeSelector(false);
    }, 300);

    const currentSizeSelector = sizeSelectorRef.current;

    const handleMouseLeave = (event: MouseEvent) => {
      if (currentSizeSelector && !currentSizeSelector.contains(event.relatedTarget as Node)) {
        debouncedClose()
      }
    };

    const handleMouseEnter = () => {
      // Cancel the debounce if the mouse re-enters
      debouncedClose.cancel?.();
    };

    if (showSizeSelector && currentSizeSelector) {
      currentSizeSelector.addEventListener('mouseleave', handleMouseLeave);
      currentSizeSelector.addEventListener('mouseenter', handleMouseEnter);
    }

    return () => {
      if (currentSizeSelector) {
        currentSizeSelector.removeEventListener('mouseleave', handleMouseLeave)
        currentSizeSelector.removeEventListener('mouseenter', handleMouseEnter)
      }
      debouncedClose.cancel?.() // Ensure cleanup of the debounce
    }
  }, [showSizeSelector, currentVariant, variants])

  const ComingSoonModalContent = () => {
    const [email, setEmail] = useState('');
    const [submitMessage, setSubmitMessage] = useState(false);
    const [isError, setIsError] = useState(false);
    const handleNotifySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${product.klaviyoApiKey}`, {
          method: 'POST',
          headers: {
            'accept': 'application/vnd.api+json',
            'revision': '2025-01-15',
            'content-type': 'application/vnd.api+json'
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email
                    }
                  }
                },
                "channels": ["EMAIL"],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${currentVariant.id}`
                  }
                }
              }
            }
          })
        });
        if(!response.ok){
          setIsError(true);
          throw new Error(`Response status: ${response.status}`);
        }
        setSubmitMessage(true);
        setEmail("");
      } catch (error) {
        setIsError(true);
        console.error('Subscription failed:', error);
      }
    };
    return (
      <div className="bg-white h-80 rounded-lg min-w-80">
        <a onClick={closeModal}></a>
        <form onSubmit={handleNotifySubmit} className="flex flex-col justify-center px-3 h-full">
          {submitMessage && (
            <p className="body-sm">Great Job. We will notify you when the product is available.</p>
          )}
          {isError && (
            <p className="body-sm" >Error, please try again later! If issues persist, please reach out to customer support.</p>
          )}
          {!submitMessage && (
            <>
              <p className="h2 font-appleGaramond mb-4">Coming Soon</p>
              <p className="body-sm mb-2">We will notify you when this product is available.</p>
              {currentVariant && <p className="body-sm mb-4">{product.title} / {currentVariant.title}</p>}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                className="rounded-full bg-white border border-gray-300 shadow-input w-full mb-4"
              />
              <button type="submit" className="button button--primary">
                <div className="rectangle-1"></div>
                <div className="button-text">
                  Notify Me
                </div>
              </button>
            </>
          )}

        </form>
      </div>
    );
  }

  if (!currentVariant) return

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <ComingSoonModalContent
        />
      </Modal>
      <div
        className={`relative flex flex-col rounded bg-gradient-grey px-2 pt-2 pb-2.5 ${animationClass} ${className}`}>
        {renderSizeSelector()}
        <div className="relative">
          {renderBadges()}
          <a href={currentVariant.url} className="block" aria-label={title}>
            {currentVariant.image?.src ? (
              <Image
                alt={currentVariant.image.alt}
                src={currentVariant.image.src}
                srcWidths={{ sm: 640 }}
                height={800}
                width={640}
                blendMode="multiply"
                classes="w-full aspect-[300/255] object-contain mt-4.5"
              />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: placeholder_svg || '' }} className="w-full h-full bg-white" />
            )}
          </a>
        </div>

        <div className="min-h-9 -mt-4.5">{variants && variants.length > 1 && renderThumbnails()}</div>
        <a href={currentVariant.url} className="block" aria-label={title}>
          <div className="flex flex-col justify-between h-full">
            {title && <p className="util-md text-black mt-5 text-center">{title}</p>}
            {/*{renderFilteredOptions()}*/}
            {product.priceVaries ? (
              <>
                <span className="sr-only">Price range: </span>
                {!thumbnailActive ? (
                  <p className="util-lg mt-4 text-black text-center">
                    {product.priceMin}
                    <span aria-hidden="true"> – </span>
                    <span className="sr-only"> to </span>
                    {product.priceMax}
                  </p>
                ) : !currentVariant.salePrice ? (
                  <p className="util-lg mt-4 text-black text-center">{currentVariant.price}</p>
                ) : (
                  <p className="util-lg mt-4 flex items-center justify-center gap-4">
                    <span className="text-inverse line-through font-normal"
                          aria-hidden="true">{currentVariant.price}</span>
                    <span className="text-black" aria-hidden="true">{currentVariant.salePrice}</span>
                    <span
                      className="text-inverse line-through font-normal sr-only">The Original Price was {currentVariant.price}</span>
                    <span className="text-black sr-only">The Sale Price is {currentVariant.salePrice}</span>
                  </p>
                )}
              </>
            ) : !currentVariant.salePrice ? (
              <p className="util-lg mt-4 text-black text-center">{currentVariant.price}</p>
            ) : (
              <p className="util-lg mt-4 flex items-center justify-center gap-4">
                <span className="text-inverse line-through font-normal" aria-hidden="true">{currentVariant.price}</span>
                <span className="text-black" aria-hidden="true">{currentVariant.salePrice}</span>
                <span
                  className="text-inverse line-through font-normal sr-only">The Original Price was {currentVariant.price}</span>
                <span className="text-black sr-only">The Sale Price is {currentVariant.salePrice}</span>
              </p>
            )}
          </div>
        </a>

        <div className="flex items-center justify-center">
          <div className="flex-1"></div>
          <div
            className="junip-product-summary pt-1"
            data-product-id={product.id}
          />
          <div className="flex-1 justify-items-end">
            {enableQuickAdd && (
              <AddToCartButton currentVariant={currentVariant} currentProduct={product} onClick={handleAddToCartClick}
                               handleNotifyClick={handleNotifyClick} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ProductCard
